
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Shield, Search, Plus, Info } from "lucide-react";
import { AddRestrictionSheet } from "./AddRestrictionSheet";
import { useRestrictionHierarchy } from "@/hooks/useRestrictionHierarchy";
import { ErrorBoundary } from "./ErrorBoundary";

export const RestrictionsSettingsContent = () => {
  console.log('RestrictionsSettingsContent initializing');
  
  const [searchTerm, setSearchTerm] = useState("");
  const { flatRestrictions } = useRestrictionHierarchy();

  // Filter restrictions based on search term
  const filteredRestrictions = useMemo(() => {
    if (!searchTerm.trim()) {
      return flatRestrictions;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return flatRestrictions.filter(restriction => 
      restriction.name.toLowerCase().includes(searchLower) ||
      restriction.backend_name.toLowerCase().includes(searchLower) ||
      restriction.type.toLowerCase().includes(searchLower)
    );
  }, [flatRestrictions, searchTerm]);

  console.log('RestrictionsSettingsContent rendering with filteredRestrictions:', filteredRestrictions.length);

  return (
    <ErrorBoundary>
      <TooltipProvider>
        <div className="h-full w-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Device Restrictions Settings</h2>
              <p className="text-muted-foreground">
                Manage available device feature restrictions. These restrictions are now managed through hardcoded configuration.
              </p>
            </div>
            <AddRestrictionSheet />
          </div>

          {/* Search */}
          <div className="flex items-center space-x-4 p-6 border-b">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search restrictions..."
                value={searchTerm}
                onChange={(e) => {
                  console.log('Search term changed:', e.target.value);
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
          </div>

          {/* Restrictions Table */}
          <div className="flex-1 overflow-auto">
            <ErrorBoundary>
              <Card className="m-6 mb-0">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>Available Restrictions</span>
                    <Badge variant="outline">{filteredRestrictions.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Hardcoded restriction settings that can be applied at device and level configurations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {filteredRestrictions.length === 0 ? (
                    <div className="p-8 text-center">
                      <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        {searchTerm ? 'No matching restrictions found' : 'No restrictions available'}
                      </h3>
                      <p className="text-muted-foreground">
                        {searchTerm 
                          ? 'Try adjusting your search terms or clear the search to see all restrictions.'
                          : 'Contact your administrator to configure restriction settings.'
                        }
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Restriction</TableHead>
                          <TableHead>Backend Key</TableHead>
                          <TableHead>Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRestrictions.map((restriction) => {
                          const isChild = Boolean(restriction.parent_id);
                          
                          return (
                            <TableRow key={restriction.type} className={isChild ? "bg-muted/30" : ""}>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <span className={`font-medium ${isChild ? 'ml-6 text-muted-foreground' : ''}`}>
                                    {isChild && '└─ '}{restriction.name}
                                  </span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Backend key: {restriction.backend_name}</p>
                                      {isChild && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Child restriction - automatically restricted when parent is restricted
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted px-2 py-1 rounded">
                                  {restriction.backend_name}
                                </code>
                              </TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted px-2 py-1 rounded">
                                  {restriction.type}
                                </code>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </ErrorBoundary>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50 m-6">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Restriction Management</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Device restrictions are now managed through hardcoded configuration. 
                      These settings define the available restrictions that can be applied to devices and levels.
                      Parent restrictions automatically restrict their child restrictions when disabled.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    </ErrorBoundary>
  );
};
